import axiosInstance from "core/api/axiosInstance";
import { getCommerceId } from "./authService";

export const getSemestralParkingService = async () => {
  try {
    const commerceId = getCommerceId();

    if (!commerceId) {
      return {
        success: false,
        error: "No se encontró un comercio asociado al usuario",
        data: [],
        total: 0,
        uniquePlates: 0,
        activeCount: 0,
        completedCount: 0,
        vehiclesInside: 0,
        vehiclesOutside: 0,
      };
    }

    const { data } = await axiosInstance.get(`/api/automatization/cbrs/co_prop/prkg/${commerceId}`);

    if (!data || typeof data !== "object") {
      return {
        success: false,
        error: "Respuesta inválida del servidor",
        data: [],
        total: 0,
        uniquePlates: 0,
        activeCount: 0,
        completedCount: 0,
        vehiclesInside: 0,
        vehiclesOutside: 0,
      };
    }

    const recordsArray = Array.isArray(data.data) ? data.data : [];
    const processedData = processParkingRecords(recordsArray);

    return {
      success: true,
      data: processedData.records,
      total: processedData.records.length,
      uniquePlates: processedData.uniquePlates,
      activeCount: processedData.activeCount,
      completedCount: processedData.completedCount,
      vehiclesInside: processedData.vehiclesInside,
      vehiclesOutside: processedData.vehiclesOutside,
      message: data.msm || "Registros semestrales obtenidos exitosamente",
    };
  } catch (error) {
    console.error("❌ Error al obtener registros semestrales:", error);
    return {
      success: false,
      error: error.message || "Error al obtener registros semestrales",
      data: [],
      total: 0,
      uniquePlates: 0,
      activeCount: 0,
      completedCount: 0,
      vehiclesInside: 0,
      vehiclesOutside: 0,
    };
  }
};

const processParkingRecords = (records) => {
  if (!Array.isArray(records) || records.length === 0) {
    return {
      records: [],
      uniquePlates: 0,
      activeCount: 0,
      completedCount: 0,
      vehiclesInside: 0,
      vehiclesOutside: 0,
    };
  }

  const allRecords = records;
  const uniquePlates = new Set(allRecords.map((r) => r.plate)).size;
  const vehiclesInside = allRecords.filter((r) => r.out_at === null).length;
  const vehiclesOutside = allRecords.filter((r) => r.out_at !== null).length;

  return {
    records: allRecords,
    uniquePlates,
    activeCount: vehiclesInside,
    completedCount: vehiclesOutside,
    vehiclesInside,
    vehiclesOutside,
  };
};

// Obtener la configuración del comercio (terminales)
export const getCommerceConfigService = async () => {
  try {
    const commerceId = getCommerceId();

    if (!commerceId) {
      return {
        success: false,
        error: "No se encontró un comercio asociado al usuario",
        data: null,
      };
    }

    const { data } = await axiosInstance.get(
      `/api/automatization/cbrs/co_prop/commerce/${commerceId}`
    );

    if (data && data.msm === "succesful") {
      const config = data.data?.config || {};
      const readers = config.readers || [];

      const entradaReader = readers.find((r) => r.readertype === 1);
      const salidaReader = readers.find((r) => r.readertype === 2);
      const otrosReaders = readers.filter((r) => r.readertype !== 1 && r.readertype !== 2);

      const idcopropietarie = config.idcopropietarie || data.data?.ref_table?.idcopropietarie || "";

      return {
        success: true,
        data: {
          id: data.data?.id,
          name: data.data?.name,
          ip: data.data?.ip,
          status: data.data?.status,
          idcopropietarie: idcopropietarie,
          config: config,
          readers: readers,
          entrada: entradaReader
            ? {
                idterminal: entradaReader.idterminal,
                hostname: entradaReader.hostname,
                model: entradaReader.model,
              }
            : null,
          salida: salidaReader
            ? {
                idterminal: salidaReader.idterminal,
                hostname: salidaReader.hostname,
                model: salidaReader.model,
              }
            : null,
          otrosReaders: otrosReaders,
        },
        message: data.msm || "Configuración obtenida exitosamente",
      };
    } else {
      return {
        success: false,
        error: data?.msm || "Error al obtener la configuración del comercio",
        data: null,
      };
    }
  } catch (error) {
    console.error("❌ Error al obtener configuración del comercio:", error);
    return {
      success: false,
      error: error.message || "Error al obtener configuración del comercio",
      data: null,
    };
  }
};

// Función para esperar un tiempo determinado
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Crear un nuevo ciclo (entrada de vehículo) - CON DELAY DE 2 SEGUNDOS
export const createCycleService = async (plate, idterminal, date, observation) => {
  try {
    const commerceId = getCommerceId();

    if (!commerceId) {
      return {
        success: false,
        error: "No se encontró un comercio asociado al usuario",
      };
    }

    // Convertir la fecha a formato local
    const dateObj = new Date(date);

    // Formatear en el formato que espera el backend: YYYY-MM-DD HH:mm:ss.000
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    const hours = String(dateObj.getHours()).padStart(2, "0");
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    const seconds = String(dateObj.getSeconds()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.000`;

    const payload = {
      id_commerce: Number(commerceId),
      id: "",
      type_cicle: "enter",
      plate: plate.toUpperCase(),
      idterminal: Number(idterminal),
      date: formattedDate,
      observation: observation || "",
    };

    const { data } = await axiosInstance.post(
      "/api/automatization/cbrs/co_prop/generate-cicle",
      payload
    );

    // Esperar 2 segundos antes de retornar la respuesta
    await delay(2000);

    if (data && data.msm === "succesful") {
      return {
        success: true,
        message: "Ciclo creado exitosamente",
        data: data.data,
      };
    } else {
      return {
        success: false,
        error: data?.msm || "Error al crear el ciclo",
      };
    }
  } catch (error) {
    console.error("❌ Error al crear ciclo:", error);
    return {
      success: false,
      error: error.response?.data?.msm || error.message || "Error al crear el ciclo",
    };
  }
};

// Cerrar un ciclo (salida de vehículo) - CON DELAY DE 2 SEGUNDOS
export const closeCycleService = async (id, plate, idterminal, date, observation) => {
  try {
    const commerceId = getCommerceId();

    if (!commerceId) {
      return {
        success: false,
        error: "No se encontró un comercio asociado al usuario",
      };
    }

    // Convertir la fecha a formato local
    const dateObj = new Date(date);

    // Formatear en el formato que espera el backend: YYYY-MM-DD HH:mm:ss.000
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    const hours = String(dateObj.getHours()).padStart(2, "0");
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    const seconds = String(dateObj.getSeconds()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.000`;

    const payload = {
      id_commerce: Number(commerceId),
      id: id,
      type_cicle: "out",
      plate: plate.toUpperCase(),
      idterminal: Number(idterminal),
      date: formattedDate,
      observation: observation || "",
    };

    const { data } = await axiosInstance.post(
      "/api/automatization/cbrs/co_prop/generate-cicle",
      payload
    );

    // Esperar 2 segundos antes de retornar la respuesta
    await delay(2000);

    if (data && data.msm === "succesful") {
      return {
        success: true,
        message: "Ciclo cerrado exitosamente",
        data: data.data,
      };
    } else {
      return {
        success: false,
        error: data?.msm || "Error al cerrar el ciclo",
      };
    }
  } catch (error) {
    console.error("❌ Error al cerrar ciclo:", error);
    return {
      success: false,
      error: error.response?.data?.msm || error.message || "Error al cerrar el ciclo",
    };
  }
};

export const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("es-ES", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

export const calculateDuration = (enterAt, outAt) => {
  if (!enterAt) return "N/A";
  const enter = new Date(enterAt);
  const out = outAt ? new Date(outAt) : new Date();
  if (isNaN(enter.getTime()) || isNaN(out.getTime())) return "N/A";
  const diffMs = Math.abs(out - enter);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h ${diffMins % 60}m`;
  if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m`;
  return `${diffMins}m`;
};

// Función para obtener solo la fecha (sin hora) de un string de fecha
export const getDateOnly = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  // Retornar solo la fecha en formato YYYY-MM-DD
  return date.toISOString().split("T")[0];
};

// Función para filtrar registros por rango de fechas - CORREGIDA
export const filterRecordsByDateRange = (records, startDate, endDate) => {
  if (!records || !Array.isArray(records)) {
    return [];
  }

  // Si no hay fechas, devolver todos los registros
  if (!startDate && !endDate) {
    return records;
  }

  return records.filter((record) => {
    if (!record.enter_at) {
      return false;
    }

    // Obtener solo la fecha del registro (sin hora)
    const recordDate = getDateOnly(record.enter_at);
    if (!recordDate) {
      return false;
    }

    // Comparar solo las fechas (sin horas)
    if (startDate && recordDate < startDate) {
      return false;
    }

    if (endDate && recordDate > endDate) {
      return false;
    }

    return true;
  });
};

// Función para obtener estadísticas del filtro
export const getFilterStats = (records, startDate, endDate) => {
  const filtered = filterRecordsByDateRange(records, startDate, endDate);

  return {
    total: filtered.length,
    active: filtered.filter((r) => r.out_at === null).length,
    completed: filtered.filter((r) => r.out_at !== null).length,
    uniquePlates: new Set(filtered.map((r) => r.plate)).size,
  };
};
